import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useQuestionnaireStore from "../store/useQuestionnaireStore";
import { ChevronLeft } from "lucide-react";
import Button from "../components/common/Button";
import useWarning from "../hooks/useWarningModal";

// --- DATA SOURCE ---
// IDs are alphabetical.
const preciseLocations = {
  Shoulder: {
    front: {
      image: "/Images/Shoulder/Shoulder.png",
      parts: [
        { id: "A", name: "Bursitis" },
        { id: "B", name: "SLAP Tear" },
        { id: "C", name: "RC Tear, Frozen Shoulder" },
        { id: "D", name: "Tendonitis, Impingement" },
        { id: "E", name: "Clavicle Injury" },
        { id: "F", name: "SCJ Injury" },
        { id: "G", name: "ACJ Injury" },
      ],
    },
  },

  Wrist: {
    front: {
      image: "/Images/Wrist/Volar/Volar_wrist.png",
      parts: [
        { id: "A", name: "Wrist Fracture & Pain" },
        { id: "B", name: "Carpal Tunnel & General Wrist Pain" },
        { id: "C", name: "Outer Wrist & Fracture Pain" },
      ],
    },
    back: {
      image: "/Images/Wrist/Dorsal/Dorsal_wrist.png",
      parts: [
        { id: "A", name: "Thumb-Side Wrist Pain" },
        { id: "B", name: "Thumb Tendon Pain" },
        { id: "C", name: "Central Wrist Pain" },
        { id: "D", name: "Top of Wrist Pain" },
        { id: "E", name: "Pinky-Side Wrist Pain" },
      ],
    },
  },

  Hip: {
    front: {
      image: "/Images/Hip/Front/front_hip.png",
      parts: [
        { id: "A", name: " Femoroacetabular Impingement (FAI)" },
        {
          id: "B",
          name: "Sports Hernia, Inguinal Hernia, Inguinal Nerve Entrapment",
        },
        { id: "C", name: "Hip Flexor Tendinitis" },
        {
          id: "D",
          name: "ITB syndrome, Trochanteric Bursitis, Gluteal Tendinitis",
        },
        { id: "E", name: "Osteitis Pubis, Athletic Pubalgia" },
        { id: "F", name: "Adductor Strain" },
        { id: "G", name: "Quadricep Strain, Meralgia Parasthetica" },
      ],
    },
    back: {
      image: "/Images/Hip/Back/back_hip.png",
      parts: [
        { id: "A", name: "Sacroiliac Joint Pain" },
        { id: "B", name: "Lumbar Radiculopathy/Sciatica " },
        { id: "C", name: "Piriformis Pain, Deep Gluteal Syndrome" },
        {
          id: "D",
          name: "Proximal Hamstring Tendinopathy, Ischiofemoral Impingement, Pudendal Nerve Entrapment",
        },
      ],
    },
  },

  Elbow: {
    front: {
      image: "/Images/Elbow/front/front_elbow.png",
      parts: [
        { id: "A", name: "Bicep Tendon Pain" },
        { id: "B", name: "Tennis Elbow (Lateral Epicondylitis)" },
        { id: "C", name: "Elbow Joint Pain / Arthritis" },
        { id: "D", name: "Golfer’s Elbow (Medial Epicondylitis)" },
      ],
    },
    back: {
      image: "/Images/Elbow/back/back_elbow.png",
      parts: [
        { id: "A", name: "Tricep Tendon Pain" },
        { id: "B", name: "Outer Elbow Pain" },
        { id: "C", name: "Elbow Tip Swelling (Bursitis)" },
        { id: "D", name: "Forearm Strain" },
      ],
    },
  },

  Back: {
    back: {
      image: "/Images/Back/back.png",
      parts: [
        { id: "A", name: "Upper Back Pain (Cervical)" },
        { id: "B", name: "Middle Back Pain (Thoracic)" },
        { id: "C", name: "Lower Back Pain (Lumbar)" },
        { id: "D", name: "Tailbone Pain (Sacrum & Coccyx)" },
      ],
    },
  },

  Ankle: {
    front: {
      image: "/Images/Ankle/front/front_ankle.png",
      parts: [
        { id: "A", name: "Shin Splints" },
        { id: "B", name: "High Ankle Sprain" },
        { id: "C", name: "Ankle Joint Pain / Impingement" },
        { id: "D", name: "Lateral Ankle Sprain" },
      ],
    },
    back: {
      image: "/Images/Ankle/back/back_ankle.png",
      parts: [
        { id: "A", name: "Achilles Tendinitis" },
        { id: "B", name: "Posterior Ankle Impingement" },
        { id: "C", name: "Heel Pain (Bursitis)" },
        { id: "D", name: "Calf Strain" },
      ],
    },
  },

  Knee: {
    front: {
      image: "/Images/Knee/front/front_knee.png",
      parts: [
        { id: "A", name: "Runner's Knee / Kneecap Pain" },
        { id: "B", name: "Outer Knee Pain" },
        { id: "C", name: "Jumper's Knee / Tendon Pain" },
        { id: "D", name: "Inner Knee Sprain" },
      ],
    },
    back: {
      image: "/Images/Knee/back/back_knee.png",
      parts: [
        { id: "A", name: "Hamstring Strain" },
        { id: "B", name: "Outer Back Corner Injury" },
        { id: "C", name: "Cyst Behind Knee" },
        { id: "D", name: "Calf Strain" },
        { id: "E", name: "Inner Back Corner Injury" },
      ],
    },
  },
  Neck:{
    back:{
      image:"/Images/Neck/neck.png",
      parts:[
        {id:"A", name:"Headache & Upper Neck Tension"},
        {id:"B", name:"Stiff Neck & Joint Pain"},
        {id:"C", name:"Muscle Knot & Shoulder Tension"},
        {id:"D", name:"Pinched Nerve Pain"},
      ]
    }
  }
};

const Step3_PreciseLocation = () => {
  const navigate = useNavigate();
  const {
    painLocation,
    setPreciseLocation,
    preciseLocation: selectedPartData, // Now an object {id, name}
  } = useQuestionnaireStore();

  const currentJoint = painLocation || "Shoulder"; 
  const jointData = preciseLocations[currentJoint];

  const hasFront = !!(jointData && jointData.front);
  const hasBack = !!(jointData && jointData.back);

  // Initialize view state based on available data
  const [activeView, setActiveView] = useState(hasFront ? "front" : "back");

  const showWarning = useWarning();

  useEffect(() => {
    if (!painLocation || !jointData) {
      navigate("/step-1");
    }
  }, [painLocation, jointData, navigate]);

  // Handle saving both ID and Name
  const handleSelectPart = (part) => {
    setPreciseLocation({ id: part.id, name: part.name });
  };

  const handleNext = () => {
    if (selectedPartData && selectedPartData.id) {
      console.log("Precise Location:", selectedPartData); // {id: "A", name: "..."}
      navigate("/step-4");
    } else {
      showWarning("Please select an area from the list before proceeding.");
    }
  };

  if (!jointData) return null;

  const currentViewData = jointData[activeView];
  
  // Safely check ID for styling selected state
  const selectedId = selectedPartData ? selectedPartData.id : null;

  return (
    <motion.div
      className="page step3-page-2d"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="step3-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <ChevronLeft size={24} /> Back
        </button>
        <h3>
          Select the precise area of your <strong>{currentJoint}</strong> pain.
        </h3>
      </div>

      <div className="view-tabs">
        <button
          className={activeView === "front" ? "active" : ""}
          onClick={() => setActiveView("front")}
          disabled={!hasFront}
          style={!hasFront ? { opacity: 0.5, cursor: "not-allowed" } : {}}
        >
          Front View
        </button>
        <button
          className={activeView === "back" ? "active" : ""}
          onClick={() => setActiveView("back")}
          disabled={!hasBack}
          style={!hasBack ? { opacity: 0.5, cursor: "not-allowed" } : {}}
        >
          Back View
        </button>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeView}
          className="precise-layout"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Left Panel: List of Parts */}
          <div className="parts-list">
            {currentViewData && currentViewData.parts.map((part) => (
              <button
                key={part.id}
                className={`part-button ${
                  selectedId === part.id ? "selected" : ""
                }`}
                onClick={() => handleSelectPart(part)}
                style={{ justifyContent: 'center' }} 
              >
                <span className="part-id" style={{ marginRight: 0 }}>{part.id}</span>
                {/* Name removed from display, but still in data for saving */}
              </button>
            ))}
          </div>

          {/* Right Panel: Image */}
          <div className="image-container">
            {currentViewData && (
              <img
                src={currentViewData.image}
                alt={`${currentJoint} ${activeView} view`}
              />
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="navigation-buttons">
        <Button onClick={handleNext}>Next</Button>
      </div>
    </motion.div>
  );
};

export default Step3_PreciseLocation;